/** Hosts that receive the visitor's UTM parameters besides the current one — the portals and Luxury Journeys. */
const UTM_DOMAINS = ["centralholidays.com", "chjourneys.com"];

const UTM_KEY = /^utm_/i;
const NON_PAGE_PATH = /^\/api\/|\.(?:pdf|jpe?g|png|gif|webp|svg|ico|mp4|zip|docx?|xlsx?|txt|xml|json)$/i;
const LEAD_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

type LeadField = (typeof LEAD_FIELDS)[number];

export type UtmCodes = { url: string } & Partial<Record<LeadField, string>>;

export type RequestSearchParams = Record<string, string | string[] | undefined>;

/** The `utm_*` pairs of a query string, re-serialized; empty when it has none. */
export function pickUtmParams(search: string | URLSearchParams): string {
  const utm = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (UTM_KEY.test(key) && value.trim()) utm.append(key, value);
  });
  return utm.toString();
}

/**
 * `href` with the `utm` query appended — only when it leads to a page on this
 * site or the portals, isn't an in-page anchor, and carries no UTMs of its own.
 */
export function withUtmParams(
  href: string,
  utm: string,
  currentUrl: string,
): string {
  const trimmed = href.trim();
  if (!utm || !trimmed || trimmed.startsWith("#")) return href;

  let target: URL;
  let current: URL;
  try {
    current = new URL(currentUrl);
    target = new URL(href, current);
  } catch {
    return href;
  }

  return shouldCarryUtm(target, current) ? appendQuery(href, utm) : href;
}

/** `href` with the incoming request's UTMs, for server redirects that would otherwise drop them. */
export function withRequestUtm(
  href: string,
  searchParams: RequestSearchParams,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    for (const v of [value ?? []].flat()) search.append(key, v);
  }
  return appendQuery(href, pickUtmParams(search));
}

/** The UTM fields a CRM lead expects, matched case-insensitively (ads have sent `utm_Term`). */
export function toUtmCodes(utm: string, url: string): UtmCodes {
  const codes: UtmCodes = { url };
  new URLSearchParams(utm).forEach((value, key) => {
    const field = key.toLowerCase() as LeadField;
    if (LEAD_FIELDS.includes(field)) codes[field] ??= value;
  });
  return codes;
}

function shouldCarryUtm(target: URL, current: URL): boolean {
  if (target.protocol !== "http:" && target.protocol !== "https:") return false;
  if (!isUtmHost(target.hostname, current.hostname)) return false;
  if (NON_PAGE_PATH.test(target.pathname)) return false;
  if ([...target.searchParams.keys()].some((key) => UTM_KEY.test(key))) {
    return false;
  }
  const samePage =
    target.origin === current.origin &&
    target.pathname === current.pathname &&
    target.search === current.search;
  return !(samePage && target.hash);
}

function isUtmHost(host: string, currentHost: string): boolean {
  return (
    host === currentHost ||
    UTM_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`))
  );
}

function appendQuery(href: string, query: string): string {
  if (!query) return href;
  const hashAt = href.indexOf("#");
  const path = hashAt === -1 ? href : href.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : href.slice(hashAt);
  const separator = !path.includes("?") ? "?" : /[?&]$/.test(path) ? "" : "&";
  return `${path}${separator}${query}${hash}`;
}
